import { Check, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { Avatar, LoadingRaccoon } from "../../components/primitives";
import { emojiUrlResolver, useEmojiCatalog } from "../../emoji";

export type PingPermissionManager = {
  slackUserId: string;
  name: string;
  avatarUrl?: string;
};

export type PingPermissionInfo = {
  allowed: boolean;
  reason: string;
  creator?: PingPermissionManager;
  managers: PingPermissionManager[];
  canRequest: boolean;
  hasDenial: boolean;
  activeRequest?: { id: string; status: string; ping: string };
};

export function PingPermissionCard({
  draftId,
  info,
  channelName,
  ping,
  onDismiss,
  onSent,
}: {
  draftId: string;
  info: PingPermissionInfo;
  channelName?: string;
  ping?: string;
  onDismiss: () => void;
  onSent?: () => void;
}) {
  const { catalog } = useEmojiCatalog();
  const urlFor = emojiUrlResolver(catalog);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);

  const approvers = [info.creator, ...info.managers].filter((manager): manager is PingPermissionManager =>
    Boolean(manager),
  );
  const [selected, setSelected] = useState(approvers[0]?.slackUserId ?? "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const selectedManager = approvers.find((manager) => manager.slackUserId === selected);

  const requesterEmoji = (name: string) => {
    const url = urlFor(name);
    return url ? <img className="inline-custom-emoji" src={url} alt={`:${name}:`} /> : `:${name}:`;
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onDismiss]);

  const requestPermission = async () => {
    if (!selected) return;
    setSending(true);
    setMessage(null);
    try {
      const result = await api.requestPing(draftId, selected);
      setMessage(result.message);
      onSent?.();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Couldn’t send your request. Try again.");
    } finally {
      setSending(false);
    }
  };

  const cancelAndRequest = async () => {
    setSending(true);
    setMessage(null);
    try {
      await api.cancelPing(draftId);
      await requestPermission();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Couldn’t cancel the old request. Try again.");
      setSending(false);
    }
  };

  return (
    <div className="ping-permission-layer">
      <button type="button" className="ping-permission-backdrop" aria-label="Close" onClick={onDismiss} />
      <section
        className="ping-permission-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ping-permission-title"
      >
        <header className="ping-permission-modal-head">
          <span className="ping-permission-modal-mark" aria-hidden="true">
            {requesterEmoji("ms-raising-hand")}
          </span>
          <strong id="ping-permission-title">
            {info.hasDenial ? "Pings are off for you here" : "You’re not a Channel Manager here"}
          </strong>
        </header>
        <div className="ping-permission-modal-body">
          {info.hasDenial ? (
            <p>
              The Channel Managers decided you can’t send @channel or @here in this channel. Ask them directly if that
              should change.
            </p>
          ) : message ? (
            <p>
              <strong className="ping-permission-success">{requesterEmoji("ms-smile")} Request sent</strong>
              <br />
              {message}
            </p>
          ) : info.activeRequest ? (
            confirmReplace ? (
              <>
                <p>
                  {requesterEmoji("ms-rewind")} This replaces your old request, so the person you already asked will see
                  it cancelled. Only your new pick gets the DM.
                </p>
                <div className="ping-permission-actions">
                  <button
                    type="button"
                    className="ping-permission-button"
                    disabled={sending}
                    onClick={cancelAndRequest}
                  >
                    {sending ? <LoadingRaccoon size={16} /> : requesterEmoji("pls")}{" "}
                    {sending ? "Swapping…" : "Yes, ask the new person"}
                  </button>
                  <button
                    type="button"
                    className="ping-permission-button ping-permission-button--ghost"
                    disabled={sending}
                    onClick={() => setConfirmReplace(false)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>
                  You already asked someone. We’ll let you know the moment they reply, and you’ll get a DM with a Send
                  now button.
                </p>
                <button
                  type="button"
                  className="ping-permission-button ping-permission-button--ghost"
                  onClick={() => setConfirmReplace(true)}
                >
                  Ask someone else instead
                </button>
              </>
            )
          ) : (
            <>
              <p>
                Ask a Channel Manager to approve this {ping ? <strong>@{ping}</strong> : "ping"} for{" "}
                {channelName ? <strong>#{channelName}</strong> : "this channel"}. Pick who to ask below and they’ll get
                a DM. When they accept, we’ll message you with a Send now button and your message goes out as-is.
              </p>
              {approvers.length > 0 && (
                <div className="ping-permission-ask">
                  <span className="ping-permission-ask-label">Who to ask</span>
                  <div className="ping-permission-picker">
                    <button
                      type="button"
                      className="ping-permission-picker-trigger"
                      aria-haspopup="menu"
                      aria-expanded={pickerOpen}
                      disabled={sending}
                      onClick={() => setPickerOpen((current) => !current)}
                    >
                      {selectedManager && (
                        <>
                          <Avatar name={selectedManager.name} src={selectedManager.avatarUrl} size={26} />
                          <span>{selectedManager.name}</span>
                        </>
                      )}
                      {!selectedManager && <span>Pick a Channel Manager</span>}
                      <ChevronDown size={14} />
                    </button>
                    {pickerOpen && (
                      <>
                        <button
                          type="button"
                          className="ping-permission-picker-backdrop"
                          aria-label="Close who to ask"
                          onClick={() => setPickerOpen(false)}
                        />
                        <div className="ping-permission-picker-menu" role="menu" aria-label="Who to ask">
                          {approvers.map((manager) => (
                            <button
                              type="button"
                              role="menuitemradio"
                              aria-checked={manager.slackUserId === selected}
                              key={manager.slackUserId}
                              onClick={() => {
                                setSelected(manager.slackUserId);
                                setPickerOpen(false);
                              }}
                            >
                              <Avatar name={manager.name} src={manager.avatarUrl} size={26} />
                              <span>
                                <strong>{manager.name}</strong>
                                {manager.slackUserId === info.creator?.slackUserId && (
                                  <small>Channel Manager · creator</small>
                                )}
                              </span>
                              {manager.slackUserId === selected && <Check size={14} />}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              <button
                type="button"
                className="ping-permission-button"
                disabled={sending || !info.canRequest || !selected}
                onClick={requestPermission}
              >
                {sending ? <LoadingRaccoon size={16} /> : requesterEmoji("ms-raising-hand")}{" "}
                {sending ? "Asking…" : "Request send"}
              </button>
            </>
          )}
        </div>
        <button type="button" className="ping-permission-close" aria-label="Dismiss" onClick={onDismiss}>
          <span aria-hidden="true">✕</span>
        </button>
      </section>
    </div>
  );
}
